import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Logo } from '../components/Logo';

// Public privacy policy. Kept in sync with docs/PRIVACY_POLICY.md — this is the
// hosted version linked from the App Store and Google Play listings.
const LAST_UPDATED = '1 July 2026';
const CONTACT_EMAIL = 'privacy@solaniq.ai';

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
        {children}
      </div>
    </section>
  );
}

export function Privacy() {
  return (
    <div className="min-h-screen bg-screen">
      <header className="border-b border-gray-200 dark:border-gray-700 bg-screen">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo className="h-8" />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
            Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Privacy Policy</h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Last updated: {LAST_UPDATED}
          </p>
        </div>

        <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
          SolanIQ ("SolanIQ", "we", "us") provides a mobile and web application that optimises home
          battery and solar systems against wholesale electricity prices and solar forecasts. This
          policy explains what personal information we collect, how we use it, who we share it with,
          and your rights. It is written to meet the Australian Privacy Principles (Privacy Act 1988
          (Cth)) and the disclosure requirements of the Apple App Store and Google Play.
        </p>

        <Section title="1. Who we are">
          <p>
            The SolanIQ app and service are operated by <strong>SolanIQ Pty Ltd</strong>, 14 Paringa
            Place, Bangor NSW 2234, Australia. For any privacy question or request, contact us at{' '}
            <a className="text-brand-orange hover:underline" href={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </Section>

        <Section title="2. Information we collect">
          <p>
            <strong>You provide directly:</strong> your email address and a password (stored only as
            a salted hash); site configuration (the location of your energy system, used to show it
            on a map and fetch local solar forecasts, plus your battery, solar, and provider
            settings); and any third-party integration credentials you enter (e.g. Amber, Solcast, or
            your battery), stored encrypted and used only to operate the service on your behalf.
          </p>
          <p>
            <strong>Generated by using the service:</strong> energy telemetry (battery state of
            charge, power flows, grid import/export, solar generation and consumption) and the
            optimisation plans, prices, and forecasts associated with your site.
          </p>
          <p>
            <strong>Collected automatically:</strong> crash reports and basic device information
            (device model, OS version, app version) via Google Firebase Crashlytics; a push
            notification token via Firebase Cloud Messaging; and a session token stored on your
            device to keep you signed in.
          </p>
          <p>
            <strong>Stored only on your device (never transmitted to us):</strong> if you enable
            biometric sign-in, your account credentials are held in your device's hardware-backed
            secure keychain/keystore and released only after a successful biometric check. This data
            never leaves your device and is removed when you sign out.
          </p>
        </Section>

        <Section title="3. How we use your information">
          <p>
            To provide and operate the optimisation service; authenticate you and keep your account
            secure; display your system's status, history, and recommendations; send notifications
            you have enabled; diagnose crashes and improve reliability; manage billing and
            subscriptions; and comply with legal obligations. We do <strong>not</strong> sell your
            personal information, and we do <strong>not</strong> use it for third-party advertising
            or cross-app tracking.
          </p>
        </Section>

        <Section title="4. Payments">
          <p>
            Subscription payments are processed by <strong>Stripe</strong>. Your card details are
            collected and handled directly by Stripe under its own privacy policy; we do not store
            full card numbers. We retain a Stripe customer identifier and your subscription status to
            manage your account.
          </p>
        </Section>

        <Section title="5. Who we share it with (service providers)">
          <p>
            We share data only with the providers needed to run the service, under contractual
            confidentiality obligations:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Fly.io</strong> — application and database hosting (stored in Australia, Sydney
              region).
            </li>
            <li>
              <strong>Google Firebase</strong> — crash reporting (Crashlytics) and push notifications
              (Cloud Messaging).
            </li>
            <li>
              <strong>Stripe</strong> — subscription payments.
            </li>
            <li>
              <strong>Energy data providers</strong> (e.g. Amber, AEMO, Solcast, Open-Meteo, GloBird)
              — prices and solar forecasts for your site.
            </li>
          </ul>
          <p>
            We may also disclose information where required by law, or to protect the rights, safety,
            and security of our users and service.
          </p>
        </Section>

        <Section title="6. Data retention">
          <p>
            We keep your account and site data for as long as your account is active. When you delete
            your account (see section 8), we deactivate the account and its data and retain it only
            as needed for legal, tax, or fraud-prevention purposes; you may request full erasure by
            contacting us. Diagnostic data in Crashlytics is retained according to Google's default
            retention period.
          </p>
        </Section>

        <Section title="7. Security">
          <p>
            Data is encrypted in transit (HTTPS/TLS). Passwords are stored as salted hashes and
            integration credentials are stored encrypted. Access to production systems is restricted.
            No system is perfectly secure, but we take reasonable steps to protect your information.
          </p>
        </Section>

        <Section title="8. Your rights and choices">
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Access and correction</strong> — view and update your account and site details
              in the app, or request a copy of your data from us.
            </li>
            <li>
              <strong>Account deletion</strong> — delete your SolanIQ account in the mobile app (Account
              tab → the settings cog at the top right → Delete account), on the website (Account →
              Delete account), or by emailing {CONTACT_EMAIL}. Deleting stops your sites, cancels
              your subscription and closes the account; the data is kept only as section 6
              describes, and you can ask us to erase it completely.
            </li>
            <li>
              <strong>Notifications</strong> — turn push notifications off in the app or your device
              settings.
            </li>
            <li>
              <strong>Complaints</strong> — contact us at {CONTACT_EMAIL}. If you are in Australia and
              are not satisfied with our response, you may contact the Office of the Australian
              Information Commissioner (oaic.gov.au).
            </li>
          </ul>
        </Section>

        <Section title="9. Children">
          <p>
            SolanIQ is not directed to children and is intended for adult account holders. We do not
            knowingly collect personal information from children under 16.
          </p>
        </Section>

        <Section title="10. International users">
          <p>
            Our systems are hosted in Australia. If you access the service from outside Australia,
            your information will be processed in Australia.
          </p>
        </Section>

        <Section title="11. Changes to this policy">
          <p>
            We may update this policy from time to time. Material changes will be notified in the app
            or by email, and the "Last updated" date above will change.
          </p>
        </Section>

        <Section title="12. Contact">
          <p>
            <a className="text-brand-orange hover:underline" href={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </a>{' '}
            — SolanIQ Pty Ltd, 14 Paringa Place, Bangor NSW 2234, Australia
          </p>
        </Section>
      </main>
    </div>
  );
}
